import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, money } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Page, Field, btnGold } from "../../components/store/ui";
import { AddressSearch } from "../../components/store/AddressSearch";

const PROVINCES = [["QC", "Québec"], ["ON", "Ontario"], ["NS", "Nouvelle-Écosse"], ["NB", "Nouveau-Brunswick"], ["NL", "Terre-Neuve-et-Labrador"],
  ["PE", "Île-du-Prince-Édouard"], ["MB", "Manitoba"], ["SK", "Saskatchewan"], ["AB", "Alberta"], ["BC", "Colombie-Britannique"],
  ["YT", "Yukon"], ["NT", "Territoires du Nord-Ouest"], ["NU", "Nunavut"]];
const POSTAL = { QC: "GHJ", ON: "KLMNP", NS: "B", NB: "E", NL: "A", PE: "C", MB: "R", SK: "S", AB: "T", BC: "V", YT: "Y", NT: "X", NU: "X" };
const postalOk = (pc, prov) => /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(pc.trim()) && POSTAL[prov].includes(pc.trim()[0].toUpperCase());

export default function Checkout() {
  const { cart, locale, user, t, config } = useApp();
  const [first, ...rest] = (user?.name || "").split(" ");
  const [a, setA] = useState({ first_name: first || "", last_name: rest.join(" "), line1: "", line2: "", city: "", province: "QC", postal_code: "", phone: "" });
  const [email, setEmail] = useState("");
  const [sum, setSum] = useState(cart);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState("");
  const key = useMemo(() => `ck_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`, []);
  const on = (k) => (e) => setA({ ...a, [k]: e.target.value });
  useEffect(() => {
    if (user?.name) {
      const [f, ...r] = user.name.split(" ");
      setA((s) => (s.first_name ? s : { ...s, first_name: f, last_name: r.join(" ") }));
    }
  }, [user]);
  useEffect(() => {
    api.get("/cart", { params: { market: locale.market, province: a.province, promo } }).then((r) => setSum(r.data)).catch((e) => { toast.error(errMsg(e), { id: "promo-err" }); setPromo(""); });
  }, [a.province, locale.market, cart.items.length, promo]);
  const pcBad = a.postal_code.length >= 6 && !postalOk(a.postal_code, a.province);
  const [payUrl, setPayUrl] = useState("");
  const pay = async (e) => {
    e.preventDefault();
    if (!postalOk(a.postal_code, a.province)) { toast.error("Le code postal ne correspond pas à la province choisie", { id: "checkout-err" }); return; }
    const inFrame = window.self !== window.top;
    const tab = inFrame ? window.open("", "_blank") : null;
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", { market: locale.market, origin_url: window.location.origin, idempotency_key: key, promo_code: promo, ...(!user && { email }), address: { ...a, country: "CA" } });
      if (!inFrame) { window.location.href = data.checkout_url; return; }
      if (tab) { tab.location.href = data.checkout_url; }
      setPayUrl(data.checkout_url);
      setBusy(false);
    } catch (err) {
      if (tab) tab.close();
      toast.error(errMsg(err), { id: "checkout-err" });
      setBusy(false);
    }
  };
  if (!cart.items.length) {
    return (
      <Page kicker="Paiement sécurisé" title={t("checkout")}>
        <p className="text-muted-foreground" data-testid="checkout-empty">Votre panier est vide.</p>
        <Link to="/shop" className={`${btnGold} inline-block mt-6`} data-testid="checkout-empty-shop">Découvrir la boutique</Link>
      </Page>
    );
  }
  return (
    <Page kicker="Paiement sécurisé" title={t("checkout")}>
      <form onSubmit={pay} className="grid lg:grid-cols-[1fr_380px] gap-12 [&>*]:min-w-0">
        <div className="grid sm:grid-cols-2 gap-5 content-start [&>*]:min-w-0">
          {!user && (
            <div className="sm:col-span-2 grid sm:grid-cols-2 gap-5 border-b border-black/10 pb-6" data-testid="guest-block">
              <Field label="Courriel" testid="checkout-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              <p className="text-xs text-muted-foreground self-end">Aucun compte à créer : nous l'ouvrons pour vous avec votre courriel et votre téléphone afin de suivre la commande. Déjà client ? <Link to="/login?next=/checkout" className="underline" data-testid="checkout-login-link">Se connecter</Link></p>
            </div>
          )}
          <Field label="Prénom" testid="checkout-first-name" required value={a.first_name} onChange={on("first_name")} />
          <Field label="Nom" testid="checkout-last-name" required value={a.last_name} onChange={on("last_name")} />
          {config?.address_lookup && <AddressSearch onPick={(x) => setA((s) => ({ ...s, ...x }))} />}
          <div className="sm:col-span-2"><Field label="Numéro civique et rue" testid="checkout-line1" required value={a.line1} onChange={on("line1")} placeholder="1325, rue des Forges" /></div>
          <div className="sm:col-span-2"><Field label="Appartement, bureau (facultatif)" testid="checkout-line2" value={a.line2} onChange={on("line2")} /></div>
          <Field label="Ville" testid="checkout-city" required value={a.city} onChange={on("city")} />
          <label className="block"><span className="label-xs">Province</span>
            <select data-testid="checkout-province" value={a.province} onChange={on("province")} className="mt-1.5 w-full border border-black/10 bg-transparent px-3 py-2.5 text-sm">
              {PROVINCES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            </select>
          </label>
          <div>
            <Field label="Code postal" testid="checkout-postal" required value={a.postal_code} onChange={on("postal_code")} placeholder="G9A 5L1" />
            {pcBad && <p className="text-xs text-red-700 mt-1" data-testid="checkout-postal-error">Code postal invalide pour cette province</p>}
          </div>
          <Field label="Téléphone (+1)" testid="checkout-phone" required value={a.phone} onChange={on("phone")} placeholder="819 555-0123" />
          <div className="sm:col-span-2 text-xs text-muted-foreground" data-testid="checkout-country">Pays : Canada — livraison au Canada uniquement. Vous serez redirigé vers Stripe : aucune donnée bancaire n'est conservée par la boutique.</div>
        </div>
        <aside className="bg-[#14161D] border border-white/10 p-8 h-fit text-sm space-y-3" data-testid="checkout-summary">
          {sum.items.map((i) => <div key={i.variant_id} className="flex justify-between gap-3"><span className="text-muted-foreground">{i.qty} × {i.title} ({i.variant_name})</span><span className="font-mono">{money(i.line_total, sum.currency, locale.lang)}</span></div>)}
          <div className="flex justify-between border-t border-white/10 pt-3"><span>Livraison</span><span>incluse</span></div>
          {sum.discount > 0 && <div className="flex justify-between" data-testid="checkout-discount"><span>Code {sum.promo}</span><span className="font-mono">−{money(sum.discount, sum.currency, locale.lang)}</span></div>}
          <div className="flex gap-2 pt-1">
            <input data-testid="promo-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code promo" className="flex-1 min-w-0 border border-black/10 bg-transparent px-3 py-2 text-sm uppercase" />
            <button type="button" data-testid="promo-apply" onClick={() => setPromo(code.trim().toUpperCase())} className="px-4 border border-black/20 text-sm hover:bg-black/5 transition-colors">Appliquer</button>
          </div>
          {(sum.taxes || []).map((x) => <div key={x.name} className="flex justify-between" data-testid={`checkout-tax-${x.name}`}><span>{x.name}</span><span className="font-mono">{money(x.amount, sum.currency, locale.lang)}</span></div>)}
          <div className="flex justify-between"><span>Retours</span><span>30 jours</span></div>
          <div className="flex justify-between text-base pt-2"><span>{t("total")}</span><span className="font-mono" data-testid="checkout-total">{money(sum.total, sum.currency, locale.lang)}</span></div>
          <button data-testid="checkout-pay-button" disabled={busy || !sum.items.length} className={`${btnGold} w-full mt-4`}>{busy ? "Redirection…" : t("pay")}</button>
          {payUrl && <a href={payUrl} target="_blank" rel="noopener noreferrer" data-testid="checkout-open-stripe" className="block text-center underline text-sm mt-3">La page de paiement ne s'est pas ouverte ? Cliquez ici pour payer</a>}
        </aside>
      </form>
    </Page>
  );
}
